import { useCallback, useMemo, useRef, useState } from 'react';
import { ButtonVariants, Table } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { ConfirmDialog } from '@/components/feedback';
import { buildTableLabels } from '@/config/tableLabels';
import { useCurrentUser, useTableState, useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import {
  useGetAccessUsersQuery,
  useInviteUserMutation,
  useRemoveUserMutation,
  useRevokeInvitationMutation,
  useSendInvitationReminderMutation,
  useUpdateUserMutation,
} from '../api/adminApi';
import { buildAccessManagerColumns } from '../constants/accessManagerColumns';
import { EMPTY_USER_FORM, USER_FIELDS } from '../constants/admin.constants';
import { buildUserFilterDefinitions } from '../constants/adminFilters';
import { buildUserPermissions } from '../constants/userActions';
import { buildRoleOptions } from '../constants/userRoles';
import { toUserFormValues } from '../mappers/adminMappers';
import { SiteAccessModal } from './SiteAccessModal';
import { UserModal } from './UserModal';

const getUserRowId = (row) => row.id;
const noop = () => {};
// The design gives the column 273px; Table's estimate clips "Sending reminder...".
const ACTIONS_WIDTH = 273;

const toOptions = (entries) => entries.map((entry) => ({ value: entry.id, label: entry.name }));

export const AccessManagerTable = () => {
  const t = useTranslations();
  const me = useCurrentUser();
  const table = useTableState();

  const { data, isFetching } = useGetAccessUsersQuery(table.queryParams);
  const rows = data?.rows ?? EMPTY_LIST;

  const [inviteUser, { isLoading: isInviting }] = useInviteUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [sendReminder] = useSendInvitationReminderMutation();
  const [removeUser] = useRemoveUserMutation();
  const [revokeInvitation] = useRevokeInvitationMutation();

  // `key` remounts the form, so "Send and add another user" opens on an empty one.
  const [editor, setEditor] = useState(null);
  const editorKeyRef = useRef(0);
  const [inviteDraft, setInviteDraft] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [accessTarget, setAccessTarget] = useState(null);
  const [remindingIds, setRemindingIds] = useState(() => new Set());

  const permissions = useMemo(() => buildUserPermissions(me), [me]);

  const siteOptions = useMemo(() => toOptions(me?.sites ?? EMPTY_LIST), [me?.sites]);
  const storageOptions = useMemo(() => toOptions(me?.storages ?? EMPTY_LIST), [me?.storages]);
  const roleOptions = useMemo(
    () => buildRoleOptions(t, me?.role, editor?.row?.role),
    [t, me?.role, editor?.row?.role],
  );

  const openEditor = useCallback((row, values) => {
    editorKeyRef.current += 1;
    setEditor({ row, values, key: editorKeyRef.current });
  }, []);

  const handleAdd = useCallback(() => openEditor(null, EMPTY_USER_FORM), [openEditor]);
  const handleEdit = useCallback((row) => openEditor(row, toUserFormValues(row)), [openEditor]);
  const handleCloseEditor = useCallback(() => setEditor(null), []);

  const handleSubmitUser = useCallback(
    async (values) => {
      if (!editor?.row) {
        setInviteDraft(values);
        setEditor(null);
        return;
      }
      try {
        await updateUser({ id: editor.row.id, values }).unwrap();
        setEditor(null);
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [editor, updateUser],
  );

  const sendInvite = useCallback(
    async (addAnother) => {
      const draft = inviteDraft;
      setInviteDraft(null);
      try {
        await inviteUser(draft).unwrap();
        if (addAnother) openEditor(null, EMPTY_USER_FORM);
      } catch {
        // Reopen on the typed values so the user can fix what the server rejected.
        openEditor(null, draft);
      }
    },
    [inviteDraft, inviteUser, openEditor],
  );

  const handleSendInvite = useCallback(() => sendInvite(false), [sendInvite]);
  const handleSendAndAddAnother = useCallback(() => sendInvite(true), [sendInvite]);
  const handleCancelInvite = useCallback(() => setInviteDraft(null), []);

  const handleSendReminder = useCallback(
    async (row) => {
      setRemindingIds((prev) => new Set(prev).add(row.id));
      try {
        await sendReminder(row.id).unwrap();
      } catch {
        // notifyApiErrors already surfaced it.
      } finally {
        setRemindingIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }
    },
    [sendReminder],
  );

  const handleConfirmRemove = useCallback(async () => {
    const id = removeTarget?.id;
    setRemoveTarget(null);
    if (id == null) return;
    try {
      await removeUser(id).unwrap();
    } catch {
      // notifyApiErrors already surfaced it.
    }
  }, [removeTarget, removeUser]);

  const handleConfirmRevoke = useCallback(async () => {
    const id = revokeTarget?.id;
    setRevokeTarget(null);
    if (id == null) return;
    try {
      await revokeInvitation(id).unwrap();
    } catch {
      // notifyApiErrors already surfaced it.
    }
  }, [revokeTarget, revokeInvitation]);

  const handleCancelRemove = useCallback(() => setRemoveTarget(null), []);
  const handleCancelRevoke = useCallback(() => setRevokeTarget(null), []);
  const handleCloseAccess = useCallback(() => setAccessTarget(null), []);

  const rowActions = useMemo(
    () => [
      {
        id: 'edit',
        label: t('COMMON_EDIT'),
        variant: ButtonVariants.tertiary,
        hidden: (row) => !permissions.canEdit(row),
        onClick: handleEdit,
      },
      {
        id: 'remove',
        label: t('COMMON_REMOVE'),
        variant: ButtonVariants.tertiary,
        hidden: (row) => !permissions.canRemove(row),
        onClick: setRemoveTarget,
      },
      {
        id: 'send-reminder',
        label: t('ADMIN_SEND_REMINDER'),
        variant: ButtonVariants.tertiary,
        hidden: (row) => !permissions.canRemind(row) || remindingIds.has(row.id),
        onClick: handleSendReminder,
      },
      {
        id: 'sending-reminder',
        label: t('ADMIN_SENDING_REMINDER'),
        variant: ButtonVariants.tertiary,
        disabled: true,
        hidden: (row) => !remindingIds.has(row.id),
        onClick: noop,
      },
      {
        id: 'revoke',
        label: t('ADMIN_REVOKE'),
        variant: ButtonVariants.tertiary,
        hidden: (row) => !permissions.canRevoke(row),
        onClick: setRevokeTarget,
      },
    ],
    [t, permissions, remindingIds, handleEdit, handleSendReminder],
  );

  const toolbarActions = useMemo(
    () =>
      permissions.canInvite
        ? [
            {
              id: 'add-user',
              label: t('ADMIN_ADD_USER'),
              icon: Plus,
              variant: ButtonVariants.primary,
              onClick: handleAdd,
            },
          ]
        : EMPTY_LIST,
    [t, permissions.canInvite, handleAdd],
  );

  const columns = useMemo(
    () =>
      buildAccessManagerColumns(t, {
        serialOffset: (table.page - 1) * table.pageSize,
        onViewAccess: setAccessTarget,
      }),
    [t, table.page, table.pageSize],
  );

  const filterDefinitions = useMemo(() => buildUserFilterDefinitions(t), [t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const noResults = useMemo(
    () => ({
      title: t('ADMIN_USERS_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  // <Table> reports sort as { id, dir }; useTableState stores { field, direction }.
  const handleSortChange = useCallback(
    (next) => {
      table.setSort(next ? { field: next.id, direction: next.dir } : null);
      table.setPage(1);
    },
    [table],
  );

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const handleFilterChange = useCallback(
    (values) => {
      table.setFilters(values);
      table.setPage(1);
    },
    [table],
  );

  const inviteEmail = inviteDraft?.[USER_FIELDS.email]?.trim() ?? '';

  return (
    <>
      <Table
        columns={columns}
        data={rows}
        total={data?.totalCount ?? 0}
        loading={isFetching}
        getRowId={getUserRowId}
        searchable
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        filters={filterDefinitions}
        onFilterChange={handleFilterChange}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        toolbarActions={toolbarActions}
        rowActions={rowActions}
        actionsHeader={t('FIELD_ACTION')}
        actionsWidth={ACTIONS_WIDTH}
        noResults={noResults}
        labels={tableLabels}
      />

      <UserModal
        key={editor?.key}
        open={editor != null}
        isEdit={Boolean(editor?.row)}
        initialValues={editor?.values ?? EMPTY_USER_FORM}
        roleOptions={roleOptions}
        siteOptions={siteOptions}
        storageOptions={storageOptions}
        saving={isInviting || isUpdating}
        onSubmit={handleSubmitUser}
        onClose={handleCloseEditor}
      />

      <ConfirmDialog
        open={inviteDraft != null}
        title={t('ADMIN_SEND_INVITATION')}
        description={t('ADMIN_SEND_INVITATION_DESCRIPTION', { email: inviteEmail })}
        confirmLabel={t('ADMIN_SEND_INVITATION')}
        extraLabel={t('ADMIN_SEND_AND_ADD_ANOTHER')}
        onExtra={handleSendAndAddAnother}
        onConfirm={handleSendInvite}
        onCancel={handleCancelInvite}
      />

      <ConfirmDialog
        open={removeTarget != null}
        title={t('ADMIN_REMOVE_USER_TITLE', { name: removeTarget?.displayName })}
        description={t('ADMIN_REMOVE_USER_DESCRIPTION', { name: removeTarget?.displayName })}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />

      <ConfirmDialog
        open={revokeTarget != null}
        title={t('ADMIN_REVOKE_TITLE', { name: revokeTarget?.displayName })}
        description={t('ADMIN_REVOKE_DESCRIPTION', { email: revokeTarget?.email })}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmRevoke}
        onCancel={handleCancelRevoke}
      />

      <SiteAccessModal user={accessTarget} onClose={handleCloseAccess} />
    </>
  );
};
